import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import "@/components/site/ChapterPill.css";

// Editorial chapter marker for the homepage. Treated as a momentary
// orientation cue: shows on chapter change, on scroll-up, then quietly
// fades out a couple of seconds after the user stops moving.
//
// On mobile/tablet (≤880px) the pill switches to a collapsible
// presentation: a single rounded glass capsule that shows the current
// chapter name and a hamburger glyph; tapping expands a compact list
// of chapters with the active one emphasised. Translucency, light/dark
// switching, and visibility behaviour are unchanged from desktop.

const CHAPTERS = [
  { label: "Philosophy", target: "philosophy-section" },
  { label: "Work", target: "scenes-work-section" },
  { label: "Press", target: "publications-section" },
  { label: "Process", target: "process-section" },
  { label: "Studio", target: "studio-section" },
];

const SECTION_TO_CHAPTER = {
  "philosophy-section": 0,

  "scenes-work-section": 1,
  "selected-work-section": 1,
  "view-all-work-section": 1,

  "press-section": 2,
  "publications-section": 2,

  "process-section": 3,

  "studio-section": 4,
  "we-shape-section": 4,
  "services-section": 4,
};

const HIDE_SECTIONS = [
  "hero-section",
  "conversation-section",
  "footer-section",
];

// Sections that need light-on-dark text inverted to dark-on-light for
// readability over a bright surface.
const LIGHT_SURFACE_SECTIONS = ["philosophy-section"];

const HIDE_DELAY_MS = 2500;
const SCROLL_DELTA = 4; // ignore micro jitter
const MOBILE_MAX = 880;

const findByTestId = (id) =>
  document.querySelector(`[data-testid="${id}"]`);

const anchorIn = (id, y) => {
  const el = findByTestId(id);
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  const top = rect.top + window.scrollY;
  const bottom = top + rect.height;
  return y >= top && y < bottom;
};

// True when the element is currently visible anywhere in the viewport.
// Used for HIDE_SECTIONS — short blocks like the conversation CTA are
// easy to miss with a single anchor-line check.
const inViewport = (id) => {
  const el = findByTestId(id);
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.top < window.innerHeight * 0.9 && r.bottom > 0;
};

export default function ChapterPill() {
  const reducedMotion = useReducedMotion();
  const [active, setActive] = useState(-1);
  const [visible, setVisible] = useState(false);
  const [onLight, setOnLight] = useState(false);

  // Mobile state — viewport breakpoint + collapsible expansion + the
  // global "mobile nav menu is open" flag that lives on <body>.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${MOBILE_MAX}px)`).matches;
  });
  const [expanded, setExpanded] = useState(false);
  const [navMenuOpen, setNavMenuOpen] = useState(false);

  // Refs so the scroll handler can read live values without re-binding.
  const lastYRef = useRef(typeof window !== "undefined" ? window.scrollY : 0);
  const lastChapterRef = useRef(-1);
  const hideTimerRef = useRef(null);
  const hoverRef = useRef(false);
  const rootRef = useRef(null);

  // Track viewport: switch between desktop and mobile presentations.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);
    const onChange = (e) => {
      setIsMobile(e.matches);
      if (!e.matches) setExpanded(false);
    };
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Observe the global mobile-menu open flag set by HlsMobileMenu on
  // <body>. When the nav menu is open the chapter pill must step
  // aside completely so the two never overlap or compete.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const read = () =>
      setNavMenuOpen(document.body.dataset.mobileMenuOpen === "true");
    read();
    const obs = new MutationObserver(read);
    obs.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-mobile-menu-open"],
    });
    return () => obs.disconnect();
  }, []);

  // When the nav menu opens, force the expanded pill closed so it
  // doesn't linger behind the menu overlay.
  useEffect(() => {
    if (navMenuOpen) setExpanded(false);
  }, [navMenuOpen]);

  useEffect(() => {
    let raf = null;

    const armHide = () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        if (!hoverRef.current) setVisible(false);
      }, HIDE_DELAY_MS);
    };

    const clearHide = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };

    const compute = () => {
      raf = null;
      const y = window.scrollY;
      const dy = y - lastYRef.current;
      lastYRef.current = y;

      const anchor = y + window.innerHeight * 0.4;

      // Hard-hide zones — hero / conversation / footer.
      for (const id of HIDE_SECTIONS) {
        if (inViewport(id)) {
          clearHide();
          setVisible(false);
          lastChapterRef.current = -1;
          return;
        }
      }

      // Also hide near the very bottom of the document.
      const docH = document.documentElement.scrollHeight;
      if (y + window.innerHeight >= docH - 120) {
        clearHide();
        setVisible(false);
        lastChapterRef.current = -1;
        return;
      }

      // Resolve active chapter.
      let nextActive = -1;
      for (const [id, chapter] of Object.entries(SECTION_TO_CHAPTER)) {
        if (anchorIn(id, anchor)) nextActive = chapter;
      }

      if (nextActive === -1) {
        clearHide();
        setVisible(false);
        lastChapterRef.current = -1;
        return;
      }

      const chapterChanged = nextActive !== lastChapterRef.current;
      const scrolledUp = dy < -SCROLL_DELTA;

      setOnLight(LIGHT_SURFACE_SECTIONS.some((id) => anchorIn(id, anchor)));
      setActive(nextActive);
      lastChapterRef.current = nextActive;

      if (chapterChanged || scrolledUp) {
        setVisible(true);
        armHide();
      }
    };

    const onScroll = () => {
      if (raf != null) return;
      raf = requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf != null) cancelAnimationFrame(raf);
      clearHide();
    };
  }, []);

  // While the mobile expanded chapter list is open, suppress the
  // auto-hide so the user can finish their tap.
  useEffect(() => {
    if (!expanded) return undefined;
    hoverRef.current = true;
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    return () => {
      hoverRef.current = false;
    };
  }, [expanded]);

  // Esc closes the expanded mobile list.
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  // Tap outside closes the expanded mobile list.
  useEffect(() => {
    if (!expanded) return undefined;
    const onDown = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setExpanded(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [expanded]);

  // Collapse on active chapter change — keeps the list from lingering
  // open while the user scrolls into a new section.
  useEffect(() => {
    if (!expanded) return;
    setExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const goToChapter = (target) => {
    const el = findByTestId(target);
    if (!el) return;
    el.scrollIntoView({
      behavior: reducedMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  const onEnter = () => {
    hoverRef.current = true;
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };
  const onLeave = () => {
    if (expanded) return; // honour the open list
    hoverRef.current = false;
    if (visible) {
      hideTimerRef.current = setTimeout(
        () => setVisible(false),
        HIDE_DELAY_MS
      );
    }
  };

  const shown = visible && !navMenuOpen;
  const activeLabel = active >= 0 ? CHAPTERS[active]?.label : "";

  // -------- Mobile / tablet (≤880px) presentation --------
  if (isMobile) {
    return (
      <div
        ref={rootRef}
        className={[
          "chapter-pill",
          "chapter-pill--mobile",
          shown ? "is-shown" : "",
          expanded ? "is-expanded" : "",
          onLight ? "is-on-light" : "",
        ].join(" ")}
        role="navigation"
        aria-label="Homepage chapters"
        aria-hidden={!shown}
        data-testid="chapter-pill"
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        <button
          type="button"
          className="chapter-pill-m__toggle"
          onClick={() => setExpanded((s) => !s)}
          aria-expanded={expanded}
          aria-controls="chapter-pill-m-list"
          aria-label={
            expanded
              ? "Close chapter menu"
              : `Chapter menu, currently on ${activeLabel || "homepage"}`
          }
          tabIndex={shown ? 0 : -1}
          data-testid="chapter-pill-mobile-toggle"
        >
          <span className="chapter-pill-m__current">{activeLabel}</span>
          <span className="chapter-pill-m__icon" aria-hidden="true">
            <svg
              className="chapter-pill-m__chevron"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 6.5l4 4 4-4" />
            </svg>
          </span>
        </button>

        <ul
          id="chapter-pill-m-list"
          className="chapter-pill-m__list"
          role="menu"
          aria-hidden={!expanded}
        >
          {CHAPTERS.map((c, i) => {
            const isActive = i === active;
            return (
              <li key={c.target} className="chapter-pill-m__item" role="none">
                <button
                  type="button"
                  role="menuitem"
                  className={[
                    "chapter-pill-m__link",
                    isActive ? "is-active" : "",
                  ].join(" ")}
                  onClick={() => {
                    setExpanded(false);
                    goToChapter(c.target);
                  }}
                  aria-current={isActive ? "true" : undefined}
                  tabIndex={shown && expanded ? 0 : -1}
                  data-testid={`chapter-pill-m-${c.label.toLowerCase()}`}
                >
                  {c.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  // -------- Desktop presentation (unchanged) --------
  return (
    <div
      ref={rootRef}
      className={[
        "chapter-pill",
        shown ? "is-shown" : "",
        onLight ? "is-on-light" : "",
      ].join(" ")}
      role="navigation"
      aria-label="Homepage chapters"
      aria-hidden={!shown}
      data-testid="chapter-pill"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <ul className="chapter-pill__list">
        {CHAPTERS.map((c, i) => {
          const isActive = i === active;
          return (
            <li
              key={c.target}
              className={[
                "chapter-pill__item",
                isActive ? "is-active" : "",
              ].join(" ")}
            >
              <button
                type="button"
                className="chapter-pill__btn"
                onClick={() => goToChapter(c.target)}
                aria-current={isActive ? "true" : undefined}
                aria-label={`Jump to ${c.label}`}
                data-testid={`chapter-pill-${c.label.toLowerCase()}`}
                tabIndex={shown ? 0 : -1}
              >
                <span className="chapter-pill__label">{c.label}</span>
                <span className="chapter-pill__dot" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
